import {
    Autocomplete,
    Box,
    CircularProgress,
    FormControl,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Checkbox,
    Tooltip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Alert,
} from "@mui/material";
import { useState } from "react";
import SkeletonTableLoader from "../../../components/table/SkeletonTableLoader";
import CustomTableContainer from "../../../components/table/CustomTableContainer";
import { useGradeChanges } from "../../../hooks/production/useGradeChange";

const TransferSalesOrderSection = ({
    title,
    hook,
    lineItemHook,
    childRollHook,
    onLineClick,
    showCheckbox = false,
    selectedRows = [],
    onRowCheckboxClick = () => { },
    onSelectAllClick = () => { },
    isSelected = () => false,
    totalSelectedWeight = 0,
    colors,
}) => {
    const loading = hook.ddloading;
    const lineItems = lineItemHook.lineItemList || [];
    const childRolls = childRollHook.childRollList || [];

    const gradeOptions = ["G1", "SL", "Repulp", "BUFFER"];

    const [gradeMap, setGradeMap] = useState({});
    const [gradeDialogOpen, setGradeDialogOpen] = useState(false);
    const [selectedGradeRow, setSelectedGradeRow] = useState(null);
    const [selectedGrade, setSelectedGrade] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [selectedLineNumber, setSelectedLineNumber] = useState(null);

    const{isLoading,updateChildRollGrade}=useGradeChanges();

    const getGradeValue = (rowId, originalGrade) => {
        return gradeMap[rowId] !== undefined ? gradeMap[rowId] : originalGrade;
    };

    const handleGradeChange = (rowId, newGrade) => {
        setGradeMap((prev) => ({
            ...prev,
            [rowId]: newGrade,
        }));
    };

    const handleChangeGradeClick = (row) => {
        setSelectedGradeRow(row);
        setSelectedGrade(getGradeValue(row.product_id, row.grade) || "");
        setGradeDialogOpen(true);
    };

    const handleGradeDialogClose = () => {
        setGradeDialogOpen(false);
        setSelectedGradeRow(null);
        setSelectedGrade("");
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setSnackbarOpen(false);
    };

    const handleLineClick = (lineNo, width, length, itemCode, gsm) => {
        setSelectedLineNumber(lineNo);
        onLineClick(lineNo, width, length, itemCode, gsm);
    };

    const handleGradeSubmit = async () => {
        if (selectedGradeRow && selectedGrade) {
            // call hook to update grade changes to database
            let company_id = localStorage.getItem('companyid') || 0;
            let plant_id = 1;
            try {
                const result = await updateChildRollGrade(company_id, plant_id, selectedGradeRow.id_product, selectedGrade);
                setSnackbarMessage("Grade updated successfully!");
                setSnackbarSeverity("success");
                setSnackbarOpen(true);

                // Clear grade cache and refresh child rolls table with fresh data
                setGradeMap({});

                // Refresh child rolls with the tracked line number
                if (childRollHook?.loadChildRollBasedOnSoNumber && hook?.selectedSalesOrderNumber?.so_number && selectedLineNumber) {
                    childRollHook.loadChildRollBasedOnSoNumber(
                        hook.selectedSalesOrderNumber,
                        selectedLineNumber
                    );
                }
            } catch (error) {
                setSnackbarMessage("Failed to update grade. Please try again.");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            }
        }
        handleGradeDialogClose();
    };

    return (
        <Grid item xl={12}>
            <Box paddingX={3}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {title}
                </Typography>

                {/* Autocomplete for Sales Order */}
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <Autocomplete
                        size="small"
                        fullWidth
                        freeSolo
                        options={hook.salesOrderNumber}
                        value={hook.selectedSalesOrderNumber}
                        loading={loading}
                        onChange={(event, newValue) => {
                            if (typeof newValue === "string") {
                                hook.setSelectedSalesOrderNumber({ so_number: newValue });
                                hook.setSelectedSalesOrderNumberID(null);                                
                            } else if (newValue) {
                                hook.setSelectedSalesOrderNumber(newValue);
                                hook.setSelectedSalesOrderNumberID(newValue.id || null);                               
                            } else {
                                hook.setSelectedSalesOrderNumber(null);
                                hook.setSelectedSalesOrderNumberID(null);                                
                            }
                        }}
                        onInputChange={(event, newInputValue) => {
                            if (newInputValue) {
                                hook.setSelectedSalesOrderNumber({ so_number: newInputValue });
                                lineItemHook.loadSalesOrderWiseLineItemBySONumber({
                                    so_number: newInputValue,
                                });
                                childRollHook.clearChildRollList();
                            }
                        }}
                        getOptionLabel={(option) =>
                            typeof option === "string" ? option : option?.so_number || ""
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Sales Order Number"
                                required
                                variant="outlined"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loading ? (
                                                <CircularProgress color="inherit" size={20} />
                                            ) : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </FormControl>


                {/* Line Item Table */}
                {lineItems.length > 0 && (
                    <CustomTableContainer colors={colors}>
                        <TableContainer>
                            {loading ? (
                                <SkeletonTableLoader rows={5} columns={6} />
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>S.No</TableCell>
                                            <TableCell>Item Code</TableCell>
                                            <TableCell>GSM</TableCell>
                                            <TableCell>Width</TableCell>
                                            <TableCell>Length</TableCell>
                                            <TableCell>Line No</TableCell>                                           
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {lineItems.map((s) => (
                                            <TableRow key={s.s_number}>
                                                <TableCell>{s.s_number}</TableCell>
                                                <TableCell>{s.item_code}</TableCell>
                                                <TableCell>{s.gsm}</TableCell>
                                                <TableCell>
                                                    <Tooltip title="Edit Line">
                                                        <span
                                                            style={{
                                                                color: "teal",
                                                                textDecoration: "underline",
                                                                cursor: "pointer",
                                                            }}
                                                            onClick={() => handleLineClick(s.line_no, s.width, s.length, s.item_code, s.gsm)}
                                                        >
                                                            {s.width}
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell>{s.length}</TableCell>
                                                <TableCell>{s.line_no}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </TableContainer>
                    </CustomTableContainer>
                )}
                {/* Child Roll Table */}
                {childRolls.length > 0 && (
                    <CustomTableContainer colors={colors}>
                        <TableContainer>
                            {loading ? (
                                <SkeletonTableLoader rows={5} columns={6} />
                            ) : (
                                <>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                {showCheckbox && (
                                                    <TableCell sx={{ width: "50px" }}>
                                                        <Checkbox
                                                            size="small"
                                                            style={{ color: "secondary" }}
                                                            indeterminate={
                                                                selectedRows.length > 0 &&
                                                                selectedRows.length < childRolls.length
                                                            }
                                                            checked={selectedRows.length === childRolls.length}
                                                            onChange={onSelectAllClick}
                                                        />
                                                    </TableCell>
                                                )}
                                                <TableCell>CHILD ROLL NUMBER</TableCell>
                                                <TableCell>MASTER REEL</TableCell>
                                                <TableCell>ITEM CODE</TableCell>
                                                <TableCell>GSM</TableCell>
                                                <TableCell>SIZE</TableCell>
                                                <TableCell>WEIGHT</TableCell>
                                                <TableCell>GRADE</TableCell>
                                                 <TableCell>Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {childRolls.map((s) => (
                                                <TableRow key={s.product_id}>
                                                    {showCheckbox && (
                                                        <TableCell>
                                                            <Checkbox
                                                                size="small"
                                                                checked={isSelected(s.id_product)}
                                                                onChange={(event) =>
                                                                    onRowCheckboxClick(event, s.id_product)
                                                                }
                                                            />
                                                        </TableCell>
                                                    )}
                                                    <TableCell>{s.product_code}</TableCell>
                                                    <TableCell>{s.product_type}</TableCell>
                                                    <TableCell>{s.item_code}</TableCell>
                                                    <TableCell>{s.gsm}</TableCell>
                                                    <TableCell>
                                                        <span
                                                            style={{
                                                                color: "teal",
                                                                textDecoration: "underline",
                                                                cursor: "pointer",
                                                            }}
                                                        >
                                                            {s.size}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>{s.weight}</TableCell>
                                                    <TableCell>{getGradeValue(s.product_id, s.grade)}</TableCell>
                                                    <TableCell>
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            onClick={() => handleChangeGradeClick(s)}
                                                        >
                                                            Change Grade
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>

                                    <Box
                                        padding={1.5}
                                        display="flex"
                                        justifyContent="space-between"
                                        backgroundColor={colors.primary[400]}
                                        mt={2}
                                    >
                                        <Typography variant="h6">
                                            Selected Rows:{" "}
                                            <strong>
                                                {showCheckbox ? selectedRows.length : childRolls.length}
                                            </strong>
                                        </Typography>
                                        <Typography variant="h6">
                                            Selected Quantity: <strong>{totalSelectedWeight}</strong>
                                        </Typography>
                                    </Box>
                                </>
                            )}
                        </TableContainer>
                    </CustomTableContainer>
                )}

                {/* Grade Change Dialog */}
                <Dialog open={gradeDialogOpen} onClose={handleGradeDialogClose} maxWidth="sm" fullWidth>
                    <DialogTitle>Change Grade</DialogTitle>
                    <DialogContent sx={{ pt: 2 }}>
                        {selectedGradeRow && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Child Roll Number:</strong> {selectedGradeRow.product_code}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    <strong>Current Grade:</strong> {getGradeValue(selectedGradeRow.product_id, selectedGradeRow.grade) || "N/A"}
                                </Typography>
                                <Autocomplete
                                    fullWidth
                                    options={gradeOptions}
                                    value={selectedGrade || null}
                                    onChange={(event, newValue) => {
                                        setSelectedGrade(newValue || "");
                                    }}
                                    getOptionLabel={(option) => option || ""}
                                    isOptionEqualToValue={(option, value) => option === value}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Grade"
                                            variant="outlined"
                                            required
                                        />
                                    )}
                                />
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleGradeDialogClose}>Cancel</Button>
                        <Button
                            onClick={handleGradeSubmit}
                            variant="contained"
                            disabled={!selectedGrade}
                        >
                            Update Grade
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Success/Error Message Snackbar */}
                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={4000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                >
                    <Alert
                        onClose={handleSnackbarClose}
                        severity={snackbarSeverity}
                        sx={{ width: "100%" }}
                    >
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </Box>
        </Grid>
    );
};

export default TransferSalesOrderSection;
